import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';

const BASE = 'app1/facebook/build-campaigns';
const POLL_MS = 3000;

const adsManagerUrl = (accountId, campaignId) =>
  `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${accountId}&selected_campaign_ids=${campaignId}`;

function Check({ ok, label }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start">
      {ok
        ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mt: '2px' }} />
        : <CancelIcon sx={{ fontSize: 16, color: 'error.main', mt: '2px' }} />}
      <Typography variant="body2" sx={{ fontSize: 13, wordBreak: 'break-word' }} color={ok ? 'text.primary' : 'error.main'}>
        {label}
      </Typography>
    </Stack>
  );
}

// Flatten a run's nested results into lines people can read: every ad set
// and ad, with Facebook's reason when one failed.
function resultLines(campaign) {
  const lines = [];
  (campaign.ad_sets || []).forEach((s) => {
    if (s.error) { lines.push({ ok: false, text: `${s.name}: ${s.error}` }); return; }
    lines.push({ ok: true, text: `${s.name}${s.reused ? ' (already built)' : ''} — ${s.zip_count?.toLocaleString?.() ?? s.zip_count} ZIPs` });
    if (s.zip_push && !s.zip_push.ok) lines.push({ ok: false, text: `  ${s.zip_push.error}` });
    if (s.zip_push?.refused) lines.push({ ok: false, text: `  Facebook refused ${s.zip_push.refused} ZIP(s); they're flagged and skipped from now on` });
    (s.ads || []).forEach((a) => {
      if (a.skipped) lines.push({ ok: false, text: `  ${a.reason}` });
      else if (a.error) lines.push({ ok: false, text: `  Ad ${a.ad_name || a.asset_id}: ${a.error}` });
      else lines.push({ ok: true, text: `  Ad ${a.ad_name}${a.reused ? ' (already built)' : ''}${a.paused ? ' (paused — not picked for this ad set)' : ''}${a.resumed ? ' (turned back on)' : ''}` });
    });
  });
  return lines;
}

export default function CampaignLaunchDialog({ open, onClose, clientSlug, clientName, onBuilt }) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adAccountId, setAdAccountId] = useState('');
  const [pageId, setPageId] = useState('');
  const [verticalIds, setVerticalIds] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetTargets, setAssetTargets] = useState({});
  const [run, setRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const [goingLive, setGoingLive] = useState({});
  const [liveIds, setLiveIds] = useState(new Set());
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const loadReadiness = useCallback(async () => {
    setLoading(true);
    try {
      const res = await defaultClient.request({ url: `${BASE}/readiness/?client_slug=${clientSlug}` });
      const data = res?.data;
      setReadiness(data);
      setAdAccountId((prev) => prev || data?.ad_accounts?.find((a) => a.active)?.account_id || data?.ad_accounts?.[0]?.account_id || '');
      setPageId((prev) => prev || data?.pages?.[0]?.page_id || '');
      setVerticalIds((prev) => (prev.length ? prev : (data?.verticals || []).filter((v) => v.ready).map((v) => v.id)));
      if (data?.latest_run) setRun(data.latest_run);
    } catch {
      toast.error('Failed to load launch readiness');
    } finally {
      setLoading(false);
    }
  }, [clientSlug]);

  useEffect(() => {
    if (open) loadReadiness();
    return stopPolling;
  }, [open, loadReadiness]);

  useEffect(() => {
    if (!open || !verticalIds.length) { setAssets([]); return; }
    defaultClient.request({ url: `${BASE}/approved-assets/?client_slug=${clientSlug}&vertical_ids=${verticalIds.join(',')}` })
      .then((res) => {
        const list = res?.data?.assets || [];
        setAssets(list);
        setAssetTargets((prev) => {
          const next = { ...prev };
          list.forEach((a) => { if (next[a.id] === undefined) next[a.id] = a.audience_target || 'both'; });
          return next;
        });
      })
      .catch(() => setAssets([]));
  }, [open, verticalIds, clientSlug]);

  const poll = useCallback((runId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await defaultClient.request({ url: `${BASE}/runs/${runId}/` });
        setRun(res?.data);
        if (['done', 'failed'].includes(res?.data?.status)) {
          stopPolling();
          loadReadiness();
          onBuilt?.();
        }
      } catch { /* keep polling */ }
    }, POLL_MS);
  }, [loadReadiness, onBuilt]);

  // Reopening the dialog mid-build picks the build back up.
  useEffect(() => {
    if (open && run && ['queued', 'running'].includes(run.status) && !pollRef.current) poll(run.id);
  }, [open, run, poll]);

  const handleBuild = async () => {
    setStarting(true);
    try {
      const res = await defaultClient.request({
        url: `${BASE}/`,
        method: 'POST',
        payload: { client_slug: clientSlug, ad_account_id: adAccountId, page_id: pageId, vertical_ids: verticalIds, asset_assignments: assetTargets },
      });
      setRun(res?.data);
      poll(res?.data?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Build could not start');
    } finally {
      setStarting(false);
    }
  };

  const handleGoLive = async (campaign) => {
    if (!window.confirm(`Turn on "${campaign.campaign_name}"? It starts spending at ${campaign.daily_budget}/day.`)) return;
    setGoingLive((p) => ({ ...p, [campaign.campaign_id]: true }));
    try {
      await defaultClient.request({ url: `${BASE}/go-live/`, method: 'POST', payload: { client_slug: clientSlug, campaign_id: campaign.campaign_id } });
      setLiveIds((p) => new Set(p).add(campaign.campaign_id));
      toast.success(`${campaign.vertical} is live`);
      onBuilt?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not turn the campaign on');
    } finally {
      setGoingLive((p) => ({ ...p, [campaign.campaign_id]: false }));
    }
  };

  const building = run && ['queued', 'running'].includes(run.status);
  const verticals = readiness?.verticals || [];
  const clientChecks = readiness?.client_checks || [];
  const selectedReady = verticalIds.length > 0 && verticalIds.every((id) => verticals.find((v) => v.id === id)?.ready);
  const canBuild = readiness?.client_ready && selectedReady && adAccountId && pageId && !building && !starting;
  const runCampaigns = run?.results?.campaigns || [];
  const runErrors = run?.results?.errors || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <RocketLaunchIcon color="primary" />
          <Typography variant="h6">Launch campaigns — {clientName}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          One broad campaign per vertical: 2 ad sets (A+ Broad, 55+ Broad), the ZIPs the nightly push targets, this month&apos;s budget,
          the approved landing page and creative. Ad sets and ads are built on inside a <strong>paused</strong> campaign — check it, then Go live.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !readiness ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : readiness && (
          <Stack spacing={2.5}>
            {/* Client-wide */}
            <Stack spacing={0.5}>
              {clientChecks.map((c) => <Check key={c.key} ok={c.ok} label={c.label} />)}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {/* Linked ones to pick from, or paste an ID — the build links it. */}
              <Autocomplete
                freeSolo fullWidth size="small" disabled={building}
                options={readiness.ad_accounts.map((a) => a.account_id)}
                getOptionLabel={(id) => {
                  const a = readiness.ad_accounts.find((x) => x.account_id === id);
                  return a ? `${a.name} · act_${a.account_id}${a.brand_code ? ` · b=${a.brand_code}` : ''}${a.active ? '' : ' (inactive)'}` : id;
                }}
                value={adAccountId || null}
                onChange={(e, v) => setAdAccountId((v || '').replace('act_', '').trim())}
                onInputChange={(e, v, reason) => { if (reason === 'input') setAdAccountId(v.replace('act_', '').trim()); }}
                renderInput={(params) => (
                  <TextField {...params} label="Ad account"
                    helperText={readiness.ad_accounts.length ? '' : 'None linked yet — paste the ad account ID'} />
                )}
              />
              <Autocomplete
                freeSolo fullWidth size="small" disabled={building}
                options={readiness.pages.map((p) => p.page_id)}
                getOptionLabel={(id) => {
                  const p = readiness.pages.find((x) => x.page_id === id);
                  return p ? `${p.page_name} · ${p.page_id}` : id;
                }}
                value={pageId || null}
                onChange={(e, v) => setPageId((v || '').trim())}
                onInputChange={(e, v, reason) => { if (reason === 'input') setPageId(v.trim()); }}
                renderInput={(params) => (
                  <TextField {...params} label="Facebook page"
                    helperText={readiness.pages.length ? '' : "None on file — paste the client's page ID"} />
                )}
              />
            </Stack>

            <Divider />

            {/* Per vertical */}
            {verticals.length === 0 ? (
              <Alert severity="warning">This client has no approved ZIPs yet.</Alert>
            ) : verticals.map((v) => {
              const checked = verticalIds.includes(v.id);
              return (
                <Box key={v.id} sx={{ border: '1px solid', borderColor: v.ready ? 'success.light' : 'divider', borderRadius: 1, p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <Checkbox size="small" sx={{ p: 0.25 }} checked={checked} disabled={!v.ready || building}
                      onChange={() => setVerticalIds((prev) => (checked ? prev.filter((id) => id !== v.id) : [...prev, v.id]))} />
                    <Typography variant="subtitle2" fontWeight={700}>{v.name}</Typography>
                    {v.live_campaign ? (
                      <Chip label="Already live" size="small" color="success" variant="outlined" sx={{ fontSize: 10.5, height: 20 }} />
                    ) : v.ready ? (
                      <Chip label="Ready" size="small" color="success" sx={{ fontSize: 10.5, height: 20 }} />
                    ) : (
                      <Chip label="Not ready" size="small" color="warning" variant="outlined" sx={{ fontSize: 10.5, height: 20 }} />
                    )}
                  </Stack>
                  {v.live_campaign ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, pl: 4 }}>
                      Live campaign: {v.live_campaign.name}. Nothing to build.
                    </Typography>
                  ) : (
                    <Stack spacing={0.4} sx={{ pl: 4 }}>
                      {v.checks.map((c) => <Check key={c.key} ok={c.ok} label={c.label} />)}
                    </Stack>
                  )}
                </Box>
              );
            })}

            {/* Creative → ad set assignment */}
            {verticalIds.length > 0 && assets.length > 0 && (
              <Box>
                <Typography variant="body2" fontWeight={600}>Which ad set each ad goes in</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Saved from the asset; change it here for this build only. Skip leaves an ad out.
                </Typography>
                <Stack spacing={0.5}>
                  {assets.map((a) => (
                    <Stack key={a.id} direction="row" alignItems="center" spacing={1.5}>
                      {a.thumbnail_url && a.asset_type !== 'video'
                        ? <Box component="img" src={a.thumbnail_url} sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 0.5 }} />
                        : <Box sx={{ width: 32, height: 32, bgcolor: 'grey.200', borderRadius: 0.5, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>vid</Box>}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" fontWeight={600} noWrap sx={{ display: 'block' }}>{a.label}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{a.vertical_name}</Typography>
                      </Box>
                      <TextField select size="small" sx={{ minWidth: 170 }} disabled={building}
                        value={assetTargets[a.id] ?? a.audience_target ?? 'both'}
                        onChange={(e) => setAssetTargets((prev) => ({ ...prev, [a.id]: e.target.value }))}>
                        <MenuItem value="both">Both ad sets</MenuItem>
                        <MenuItem value="advantage_plus">A+ Broad only</MenuItem>
                        <MenuItem value="broad_55_plus">55+ Broad only</MenuItem>
                        <MenuItem value="skip">Skip</MenuItem>
                      </TextField>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Build progress + results */}
            {run && (
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {building ? 'Building…' : run.status === 'failed' ? 'Build failed' : 'Last build'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {run.created_by ? `${run.created_by} · ` : ''}{run.created_at ? new Date(run.created_at).toLocaleString() : ''}
                  </Typography>
                </Stack>
                {building && (
                  <>
                    <LinearProgress sx={{ mb: 0.75 }} />
                    <Typography variant="caption" color="text.secondary">{run.step}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Videos can take a few minutes. You can close this — the build keeps going.
                    </Typography>
                  </>
                )}
                {run.error && <Alert severity="error" sx={{ mt: 1 }}>{run.error}</Alert>}
                {runErrors.map((e, i) => <Alert key={i} severity="error" sx={{ mt: 1 }}>{e.vertical}: {e.error}</Alert>)}
                {runCampaigns.map((c) => {
                  const lines = resultLines(c);
                  const failed = lines.some((l) => !l.ok);
                  const isLive = liveIds.has(c.campaign_id);
                  return (
                    <Box key={c.campaign_id || c.vertical} sx={{ mt: 1, p: 1.25, borderRadius: 1, border: '1px solid', borderColor: failed ? 'warning.light' : 'success.light' }}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" fontWeight={700}>{c.vertical}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.daily_budget}/day{c.reused ? ' · finished the earlier build' : ''}</Typography>
                        <Box sx={{ flex: 1 }} />
                        {c.campaign_id && (
                          <Link href={adsManagerUrl(run.ad_account_id, c.campaign_id)} target="_blank" rel="noopener noreferrer" variant="caption">
                            Open in Ads Manager
                          </Link>
                        )}
                        {c.campaign_id && !building && (
                          <Button size="small" variant="contained" color="success" disableElevation
                            disabled={isLive || goingLive[c.campaign_id]} onClick={() => handleGoLive(c)}>
                            {isLive ? 'Live' : goingLive[c.campaign_id] ? 'Turning on…' : 'Go live'}
                          </Button>
                        )}
                      </Stack>
                      <Box sx={{ mt: 0.5 }}>
                        {lines.map((l, i) => (
                          <Typography key={i} variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', color: l.ok ? 'text.secondary' : 'error.main' }}>
                            {l.ok ? '✓ ' : '✗ '}{l.text}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
                {!building && runCampaigns.some((c) => resultLines(c).some((l) => !l.ok)) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Fix what failed and press Build again — it finishes this build instead of starting over.
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleBuild} disabled={!canBuild}
          startIcon={starting || building ? <CircularProgress size={16} /> : <RocketLaunchIcon />}>
          {building ? 'Building…' : `Build ${verticalIds.length || ''} ${verticalIds.length === 1 ? 'campaign' : 'campaigns'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
